import { useState, useEffect, useRef } from "react";
import Select from "react-select"; // Import react-select
import ServiceMasterModal from "../Popup/ServiceMasterModal";
import axiosInstance from "../axiosinstance";
import { addDays, format, parse } from "date-fns";
import { ClipLoader } from "react-spinners";
import { components } from "react-select";
// import "./InvoiceForm.css";
import "./EditInvoice.css";
import "../Forms/Forms.css";
import { useNavigate, useParams } from "react-router-dom";

import { MdCancel } from "react-icons/md";
import { Snackbar, Alert as MuiAlert } from "@mui/material"; // Import MuiAlert as Alert
import { Bold } from "lucide-react";

const formatDate = (date) => {
  const d = new Date(date);
  return format(d, "dd-MM-yyyy"); // DD-MM-YYYY
};

// ✅ Add this under formatDate()
const FULL_DATE_REGEX = /^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/;

const normalizeInvoiceDateInput = (value, { allowPartial = true } = {}) => {
  if (!value) return { value: "", error: "" };

  // Replace / . with -
  let cleaned = value.trim().replace(/[./]/g, "-").replace(/-+/g, "-");

  // ❌ Catch if string starts or ends with hyphen
  if (cleaned.startsWith("-") || cleaned.endsWith("-")) {
    return {
      value: cleaned,
      error: "Invalid date format",
    };
  }

  // If it contains anything other than digits and '-', show error
  if (/[^0-9-]/.test(cleaned)) {
    return { value: cleaned, error: "Only numbers allowed" };
  }

  // ❌ NEW: Check for very long year (more than 4 digits)
  const LONG_YEAR_PATTERN = /^(\d{1,2})-(\d{5,})$/;
  if (LONG_YEAR_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid year format",
    };
  }

  const INVALID_MONTH_YEAR_PATTERN = /^(\d{1,2})-(\d{3})-(\d{3})$/;
  if (INVALID_MONTH_YEAR_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid date format. Use DD-MM-YYYY",
    };
  }

  const THREE_DIGIT_DAY_PATTERN = /^(\d{3})-(\d{1,2})-(\d{2,4})$/;
  if (THREE_DIGIT_DAY_PATTERN.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid day format. Use DD-MM-YYYY",
    };
  }

  // ❌ Explicitly catch 3-digit years like "1-4-200"
  const THREE_DIGIT_YEAR = /^(\d{1,2})-(\d{1,2})-(\d{3})$/;
  if (THREE_DIGIT_YEAR.test(cleaned)) {
    return {
      value: cleaned,
      error: "Enter full year (YYYY)",
    };
  }

  // ❌ Catch year in middle position like "12-1999-23"
  const YEAR_IN_MIDDLE = /^(\d{1,2})-(\d{4})-(\d{1,2})$/;
  if (YEAR_IN_MIDDLE.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid format. Use DD-MM-YYYY",
    };
  }

  // ❌ Catch year at start like "1999-45-45"
  const YEAR_AT_START = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
  if (YEAR_AT_START.test(cleaned)) {
    return {
      value: cleaned,
      error: "Invalid format. Use DD-MM-YYYY",
    };
  }

  // ❌ Catch 2-digit year that looks invalid like "13-12-13"
  const possibleInvalidYear = /^(\d{2})-(\d{1,2})-(\d{2})$/;
  if (possibleInvalidYear.test(cleaned)) {
    const match = cleaned.match(possibleInvalidYear);
    const firstPart = parseInt(match[1], 10);
    const thirdPart = parseInt(match[3], 10);

    if (firstPart > 12 && thirdPart <= 31) {
      return {
        value: cleaned,
        error: "Enter full year (YYYY)",
      };
    }
  }

  // ❌ Catch single digit year like "1-1-1"
  const SINGLE_DIGIT_YEAR = /^(\d{1,2})-(\d{1,2})-(\d{1})$/;
  if (SINGLE_DIGIT_YEAR.test(cleaned)) {
    return {
      value: cleaned,
      error: "Enter full year (YYYY)",
    };
  }

  // While the user is still typing (e.g. "1", "11-", "11-10-2")
  // don't force validation yet
  if (allowPartial && !FULL_DATE_REGEX.test(cleaned)) {
    return { value: cleaned, error: "" };
  }

  // Now we expect DD-M-YY or DD-M-YYYY
  const match = cleaned.match(FULL_DATE_REGEX);
  if (!match) {
    return { value: cleaned, error: "Invalid date format" };
  }

  let day = parseInt(match[1], 10);
  let month = parseInt(match[2], 10);
  const yearRaw = match[3];

  // Convert year to a proper 4-digit number for validation / due date
  let year = parseInt(yearRaw, 10);

  // 2-digit year → 2000–2099 (e.g. 25 → 2025)
  if (yearRaw.length === 2) {
    year = 2000 + year;
  }

  // Range checks
  if (month < 1 || month > 12) {
    return { value: cleaned, error: "Month must be between 1–12" };
  }
  if (day < 1 || day > 31) {
    return { value: cleaned, error: "Day must be between 1–31" };
  }

  if (day === 0 || month === 0) {
    return {
      value: cleaned,
      error: "Day and month cannot be zero",
    };
  }

  if ((day < 1 || day > 31) && (month < 1 || month > 12)) {
    return {
      value: cleaned,
      error: "Invalid day and month",
    };
  }

  // Calendar validity (Feb, 30/31, leap year, etc.)
  const jsDate = new Date(year, month - 1, day);
  if (
    jsDate.getFullYear() !== year ||
    jsDate.getMonth() + 1 !== month ||
    jsDate.getDate() !== day
  ) {
    return { value: cleaned, error: "Invalid calendar date" };
  }

  // ❌ Check for unrealistic historical dates (before 1950)
  if (year < 1950) {
    return {
      value: cleaned,
      error: "Invalid year. Please enter a valid year (1950-2099)",
    };
  }

  // Build normalized DD-MM-YYYY for non-partial usage (due date calc)
  const normalized = `${String(day).padStart(2, "0")}-${String(month).padStart(
    2,
    "0"
  )}-${year}`;

  // 👉 For typing in the field: DON'T rewrite what user typed
  if (allowPartial) {
    return { value: cleaned, error: "" };
  }

  // 👉 For due-date calculation: return normalized full year (e.g. 2025)
  return { value: normalized, error: "" };
};
   
// Same style as InvoiceForm, supports string or Date
const calculateDueDate = (invoiceDate, terms) => {
  if (!invoiceDate) return "";

  let parsedInvoiceDate;

  if (invoiceDate instanceof Date) {
    parsedInvoiceDate = invoiceDate;
  } else if (typeof invoiceDate === "string") {
    const { value: normalized, error } = normalizeInvoiceDateInput(
      invoiceDate,
      {
        allowPartial: false, // ⛔ only full valid dates here
      }
    );

    if (error || !normalized) {
      console.error("Invalid invoice date:", invoiceDate);
      return "";
    }

    const parsedValue = parse(normalized, "dd-MM-yyyy", new Date());
    if (isNaN(parsedValue.getTime())) {
      console.error("Invalid invoice date:", invoiceDate);
      return "";
    }

    parsedInvoiceDate = parsedValue;
  } else {
    return "";
  }

  let daysToAdd = 0;
  if (terms && terms !== "due" && !isNaN(parseInt(terms, 10))) {
    daysToAdd = parseInt(terms, 10);
  }

  const updatedDueDate = addDays(parsedInvoiceDate, daysToAdd);
  return format(updatedDueDate, "dd-MM-yyyy");
};

export default function EditInvoice({ isSidebarExpanded }) {
  const [items, setItems] = useState([
    {
      type: "",
      fy_id: null,
      quantity: 1,
      rate: 0,
      discount: 0,
      tax: "",
      amount: 0,
    },
  ]);
  const [isPopupOpen, setIsPopupOpen] = useState(false); // Add this line
  const [gstValue, setGstValue] = useState(false); // Default is false, you can change it to true if needed.
  const [term, setTerm] = useState("");
  const [totalAmount, setTotalAmount] = useState(0); // Total amount before discount
  const [totalDiscount, setTotalDiscount] = useState(0); // Total discount percentage
  const [totalDiscountAmount, setTotalDiscountAmount] = useState(0); // Total discount value
  const [totalAmountAfterDiscount, setTotalAmountAfterDiscount] = useState(0); // Final amount after discount
  const [totalDiscountAmountState, setTotalDiscountAmountState] = useState(0);
  const [isVisitorPopupOpen, setIsVisitorPopupOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [invoiceDateError, setInvoiceDateError] = useState("");
  const [subsidiaryId, setSubsidiaryId] = useState(null); // New state for subsidiary_id
  const [services, setServices] = useState([]);
   const [financialYears, setFinancialYears] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [servicesError, setServicesError] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(""); // New state for invoice number
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error"); // error by default
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompanyType, setSelectedCompanyType] = useState("");
  const { invoice_id } = useParams(); // Retrieve the admin ID from the URL
  const [subTotal, setSubTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasGST, setHasGST] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    company: "",
    area: "",
    landmark: "",
    office: "",
    pin: "",
    phone: "",
  });

  const [shippingAddress, setShippingAddress] = useState({
    company: "",
    address: "",
    phone: "",
  });

  const [selectedService, setSelectedService] = useState(null);
  const [showEditButton, setShowEditButton] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const invoiceDateInputRef = useRef(null);
  const [selectedSubsidary, setSelectedSubsidary] = useState("");
  const discountOptions = [
    { value: "rs", label: "₹" },
    { value: "percentage", label: "%" },
  ];
  const [discountType, setdiscountType] = useState("%");
  const [subsidaries, setSubsidaries] = useState([]);
  const customStyles = { 
    control: (base) => ({
      ...base,
      minWidth: "53px",
      maxWidth: "53px",
      height: "36px",
      padding: "0",
      borderRadius: "6px",
      fontSize: "14px",
      textAlign: "center",
      cursor: "pointer",
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      "&:hover": {
        borderColor: "#cbd5e0",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      justifyContent: "center",
      padding: "0",
    }),
    singleValue: (base) => ({
      ...base,
      margin: "0",
      color: "#4a5568",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "2px",
      color: "#a0aec0",
      "&:hover": {
        color: "#718096",
      },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({
      ...base,
      minWidth: "53px",
      fontSize: "14px",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      borderRadius: "6px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#edf2f7" : "white",
      color: "#4a5568",
      "&:hover": {
        backgroundColor: "#e2e8f0",
      },
    }),
  };
  const serviceMenuPortalTarget =
    typeof window !== "undefined" ? document.body : null;
  useEffect(() => {

    const fetchSubsidaryAndServices = async () => {
      try {
        const response = await axiosInstance.get(`/api/invoice/metadata`);
        const companyData = response.data.subsidiaries;
        const servicesData = response.data.services;
        
        setSubsidaries(companyData);
        setServices(servicesData);
        setInvoiceNumber(response.data.invoice_number);
      } catch (error) {
        console.error("❌ Failed to fetch subsidaries:", error);
      }
    };

    fetchSubsidaryAndServices();
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // 3 seconds delay

    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const response = await axiosInstance.get(`/api/financial-years`);
        
        setFinancialYears(response.data.data);
        // setLoadingServices(false);
      } catch (error) {
        // setServicesError("Failed to fetch financial years");
        // setLoadingServices(false);
      }
    };

    fetchFinancialYears();
  }, []);
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!invoice_id) return;

      try {
        const response = await axiosInstance.get(
          `/api/get_invoice_by_id/${invoice_id}`
        );
        if (response.status === 200 && response.data.data.length > 0) {
          const data = response.data.data[0];

          console.log("✅ API Response Data:", data);

          // Set client and invoice details
          setSelectedClient(data.client_name || "N/A");
          setInvoiceNumber(data.invoice_number || "N/A");
          setSelectedCompanyType(data.company_type || "N/A");
          setSubsidiaryId(data.subsidiary_id);
          setSelectedSubsidary(data.subsidiary_id);
          setHasGST(data.has_gst);
          setPaymentTerms(data.terms === 0 ? "due" : `${data.terms}`);
          // setdiscountType(data.discount_type === "%" ? "percentage" : "rs");
          setdiscountType(
            data.discount_type === "percentage" ? "percentage" : "rs"
          );

          // Set formatted dates
          setInvoiceDate(
            data.invoice_date ? formatDate(data.invoice_date) : ""
          );
          setDueDate(data.due_date ? formatDate(data.due_date) : "");

          // Set billing and shipping addresses
          setBillingAddress({
            company: data.client_company_name || "N/A",
            area: data.billing_area || "N/A",
            landmark: data.billing_landmark || "N/A",
            office: data.billing_office_number || "N/A",
            pin: data.billing_pin_code || "N/A",
            phone: data.contact_number || "N/A", // Ensure this field exists in API response
          });

          setShippingAddress({
             company: data.client_company_name || "N/A",
            area: data.billing_area || "N/A",
            landmark: data.billing_landmark || "N/A",
            office: data.billing_office_number || "N/A",
            pin: data.billing_pin_code || "N/A",
            phone: data.contact_number || "N/A",
          });

          // Map items
          const mappedItems = (data.informatiion || []).map((item) => ({
            type: item.service_name || "N/A",
            serviceId: item.service_master_id || null,
            service_provided_id: item.service_provided_id || null,
            quantity: parseFloat(item.quantity) || 0,
            rate: parseFloat(item.rate) || 0,
            discount: parseFloat(item.discount) || 0,
            tax: data.has_gst ? parseFloat(item.tax) || 0 : 0,
            amount: parseFloat(item.amount) || 0,
            fy_id: item.fy_id || null,
          }));
          
          setItems(mappedItems);

          // Debugging: Console logs for values before setting them
          console.log("🔹 Total Amount from API:", data.total_amount);
          console.log("🔹 Parsed Total Amount:", parseFloat(data.total_amount));
          console.log("🔹 Subtotal from API:", data.sub_total);
          console.log("🔹 Parsed Subtotal:", parseFloat(data.sub_total));
          console.log("🔹 Overall Discount from API:", data.overall_discount);
          console.log(
            "🔹 Parsed Overall Discount:",
            parseFloat(data.overall_discount)
          );
          console.log("🔹 Mapped Items:", mappedItems);

          // Set totals
          setTotalAmount(parseFloat(data.total_amount) || 0);
          setSubTotal(parseFloat(data.sub_total) || 0); // ✅ Added this line
          setTotalDiscount(parseFloat(data.overall_discount) || 0);
          console.log("🔹 Total Amount:", parseFloat(data.total_amount));
          console.log("🔹 Subtotal:", parseFloat(data.sub_total)); // ✅ Ensure subtotal is set
          console.log(
            "🔹 Overall Discount:",
            parseFloat(data.overall_discount)
          );
        } else {
          console.log("❌ No invoice data found");
          setError((prevState) => ({
            ...prevState,
            invoice: "Failed to fetch invoice data",
          }));
        }
      } catch (error) {
        console.error("❌ Error fetching invoice data:", error);
        setError((prevState) => ({
          ...prevState,
          invoice: "An error occurred while fetching the invoice data",
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [invoice_id]);

  // const handleServiceSelection = (index, selectedOption) => {
  //   if (selectedOption.value === "add_new_Visitor") {
  //     setIsVisitorPopupOpen(true);
  //     return;
  //   }

  //   const updatedItems = [...items];
  //   updatedItems[index].type = selectedOption.label;
  //   updatedItems[index].serviceId = selectedOption.value;

  //   const selectedService = services.find(
  //     (service) => service.service_id === selectedOption.value
  //   );

  //   if (selectedService) {
  //     updatedItems[index].rate = parseFloat(selectedService.rate);
  //     updatedItems[index].tax = hasGST
  //       ? parseFloat(selectedService.gst_rate)
  //       : 0;

  //     const gstAmount = hasGST
  //       ? (parseFloat(selectedService.rate) *
  //           parseFloat(selectedService.gst_rate)) /
  //         100
  //       : 0;

  //     updatedItems[index].gstAmount = gstAmount;
  //     updatedItems[index].totalAmount =
  //       parseFloat(selectedService.rate) + gstAmount;
  //   }

  //   // Ensure amount is updated
  //   updateAmounts(index, updatedItems);

  //   setItems(updatedItems);
  // };

  const handleAddNewService = () => {
    setIsModalOpen(true);
  };

  const handleSaveService = (newService) => {
    console.log("📥 Received New Service from Modal:", newService);

    setServices((prevServices) => [...prevServices, newService]);

    setItems((prevItems) => {
      const updatedItems = [...prevItems];
      const lastIndex = updatedItems.length - 1;

      if (lastIndex >= 0) {
        updatedItems[lastIndex].type = newService.service_name;
        updatedItems[lastIndex].serviceId = newService.service_id;
        updatedItems[lastIndex].rate = parseFloat(newService.selling_cost) || 0;

        const client = clients.find(
          (client) => client.client_id === selectedClient
        );

        if (client && client.has_gst) {
          updatedItems[lastIndex].tax = parseFloat(newService.gst_rate) || 0;
          updatedItems[lastIndex].gstAmount =
            (updatedItems[lastIndex].rate * updatedItems[lastIndex].tax) / 100;
          updatedItems[lastIndex].totalAmount =
            updatedItems[lastIndex].rate + updatedItems[lastIndex].gstAmount;
        } else {
          updatedItems[lastIndex].tax = 0;
          updatedItems[lastIndex].gstAmount = 0;
          updatedItems[lastIndex].totalAmount = updatedItems[lastIndex].rate;
        }

        updateAmounts(lastIndex, updatedItems);
      }

      return updatedItems;
    });
  };

  const handleServiceSelection = (index, selectedOption) => {
    if (!selectedOption) {
      const updatedItems = [...items];
      updatedItems[index] = {
        ...updatedItems[index],
        type: "",
        serviceId: null,
        fy_id: null,
        rate: 0,
        tax: 0,
        gstAmount: 0,
        totalAmount: 0,
      };
      setItems(updatedItems);
      return;
    }

    if (selectedOption.value === "add-new") {
      handleAddNewService();
      return;
    }

    const updatedItems = [...items];
    updatedItems[index].type = selectedOption.label;
    updatedItems[index].serviceId = selectedOption.value;

    const selectedService = services.find(
      (service) => service.service_id === selectedOption.value
    );

    if (selectedService) {
      updatedItems[index].rate = parseFloat(selectedService.rate);
      updatedItems[index].tax = hasGST
        ? parseFloat(selectedService.gst_rate)
        : 0;

      const gstAmount = hasGST
        ? (parseFloat(selectedService.rate) *
            parseFloat(selectedService.gst_rate)) /
          100
        : 0;

      updatedItems[index].gstAmount = gstAmount;
      updatedItems[index].totalAmount =
        parseFloat(selectedService.rate) + gstAmount;
    }

    updateAmounts(index, updatedItems);
    setItems(updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;

    if (field === "type") {
      const selectedService = services.find(
        (service) => service.service_name === value
      );

      if (selectedService) {
        updatedItems[index].rate = parseFloat(selectedService.rate);

        if (hasGST) {
          updatedItems[index].tax = parseFloat(selectedService.gst_rate);
          const gstAmount =
            (parseFloat(selectedService.rate) *
              parseFloat(selectedService.gst_rate)) /
            100;
          updatedItems[index].gstAmount = gstAmount;
          updatedItems[index].totalAmount =
            parseFloat(selectedService.rate) + gstAmount;
        } else {
          updatedItems[index].tax = 0;
          updatedItems[index].gstAmount = 0;
          updatedItems[index].totalAmount = parseFloat(selectedService.rate);
        }
      }
    }

    if (["quantity", "rate", "discount"].includes(field)) {
      updateAmounts(index, updatedItems);
    }

    setItems(updatedItems);
  };

  const updateAmounts = (index, updatedItems) => {
    const baseAmount = updatedItems[index].quantity * updatedItems[index].rate;
    const discountAmount = (baseAmount * updatedItems[index].discount) / 100;
    const amountAfterDiscount = baseAmount - discountAmount;

    // Only apply GST if has_gst is true
    const gstAmount = hasGST
      ? (amountAfterDiscount * updatedItems[index].tax) / 100
      : 0;

    updatedItems[index].amount = amountAfterDiscount + gstAmount;
  };

  // Update the subtotal (sum of all items' amounts)
  const updateSubtotal = (updatedItems) => {
    const newSubtotal = updatedItems.reduce(
      (sum, item) => sum + (item.totalAmount || 0),
      0
    );
    setSubTotal(newSubtotal); // Set the updated subtotal to state
  };

  const getAvailableServices = (currentIndex) => {
    const selectedServiceNames = items
      .filter((_, i) => i !== currentIndex)
      .map((item) => item.type);

    return services.filter(
      (service) => !selectedServiceNames.includes(service.service_name)
    );
  };

  // const handleInvoiceDateChange = (e) => {
  //   const selectedDate = e.target.value;
  //   setInvoiceDate(selectedDate);

  //   const date = new Date(selectedDate);

  //   if (paymentTerms === "due") {
  //     setDueDate(format(date, "dd MMM yyyy"));
  //   } else if (paymentTerms && paymentTerms.startsWith("Days")) {
  //     const daysToAdd = parseInt(paymentTerms.replace("Days", ""), 10);
  //     const calculatedDueDate = addDays(date, daysToAdd);
  //     setDueDate(format(calculatedDueDate, "dd MMM yyyy"));
  //   }
  // };

  // Watch for changes to invoiceDate or paymentTerms and calculate the due date
  useEffect(() => {
    // If date is empty or has error, don't calculate
    if (!invoiceDate || !paymentTerms || invoiceDateError) {
      setDueDate("");
      return;
    }

    // Only recalc when date *looks* complete: D-M-YY / D-M-YYYY etc.
    if (!FULL_DATE_REGEX.test(invoiceDate)) {
      return;
    }

    const newDue = calculateDueDate(invoiceDate, paymentTerms);
    setDueDate(newDue);
  }, [invoiceDate, paymentTerms, invoiceDateError]);
  // Recalculate when either of these change

  const handleInputChange = (field, value) => {
    switch (field) {
      case "selectedClient":
        setSelectedClient(value);
        break;
      case "invoiceNumber":
        setInvoiceNumber(value);
        break;
      case "invoiceDate": {
        if (!value) {
          setInvoiceDate("");
          setInvoiceDateError("Invoice Date is required");
          setDueDate("");
          return;
        }

        // Store cursor position before state update
        const input = invoiceDateInputRef.current;
        const cursorPosition = input ? input.selectionStart : 0;
        const previousValue = invoiceDate;

        // Check if user is deleting (backspace/delete)
        const isDeleting = value.length < previousValue.length;

        // Auto-insert hyphens only when typing (not deleting)
        let autoFormatted = value.replace(/[^0-9]/g, ""); // Remove non-digits

        if (!isDeleting) {
          if (autoFormatted.length >= 2) {
            autoFormatted =
              autoFormatted.slice(0, 2) + "-" + autoFormatted.slice(2);
          }
          if (autoFormatted.length >= 5) {
            autoFormatted =
              autoFormatted.slice(0, 5) + "-" + autoFormatted.slice(5);
          }
        } else {
          // When deleting, just remove hyphens and let user delete freely
          autoFormatted = value.replace(/[^0-9-]/g, "");
        }

        // Limit to DD-MM-YYYY format (10 chars)
        autoFormatted = autoFormatted.slice(0, 10);

        const { value: cleaned, error } = normalizeInvoiceDateInput(
          autoFormatted,
          {
            allowPartial: true,
          }
        );

        setInvoiceDate(cleaned);
        setInvoiceDateError(error);

        // Restore cursor position after state update
        requestAnimationFrame(() => {
          if (input && cursorPosition !== null) {
            let newPosition = cursorPosition;
            if (!isDeleting) {
              // Adjust cursor if hyphens were added during typing
              if (cursorPosition === 2 && autoFormatted.length > 2)
                newPosition = 3;
              if (cursorPosition === 5 && autoFormatted.length > 5)
                newPosition = 6;
            }
            input.setSelectionRange(newPosition, newPosition);
          }
        });
        break;
      }
      case "paymentTerms":
        setPaymentTerms(value);
        break;
      case "dueDate":
        setDueDate(value);
        break;
      default:
        break;
    }
  };
  const handleFYSelection = (index, selectedOption) => {
    // handleItemChange(index, "type", selectedOption.label);
    handleItemChange(index, "fy_id", selectedOption.value);
  };
  const addNewRow = () => {
    setItems([
      ...items,
      {
        type: "", // Default empty type
        quantity: 1,
        fy_id: null,
        rate: 0,
        discount: 0,
        tax: "",
        amount: 0,
      },
    ]);
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClient || !invoiceNumber) {
      alert("Client and Invoice Number are required!");
      return;
    }

    const invalidItems = items.some(
      (item) => !item.type || item.quantity <= 0 || item.rate <= 0
    );

    if (invalidItems) {
      alert("All items must have a valid type, quantity, and rate!");
      return;
    }

    const subtotal = items.reduce(
      (acc, item) => acc + (parseFloat(item.amount) || 0),
      0
    );

    // Convert DD-MM-YYYY to YYYY-MM-DD for backend
    const convertToBackendFormat = (dateStr) => {
      if (!dateStr) return "";
      const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (!match) return "";
      return `${match[3]}-${match[2]}-${match[1]}`; // YYYY-MM-DD
    };

    // Convert dates to backend format
    const formattedInvoiceDate = convertToBackendFormat(invoiceDate);
    const formattedDueDate = convertToBackendFormat(dueDate);

    const updatedInvoiceData = {
      invoice_number: invoiceNumber,
      invoice_date: formattedInvoiceDate,
      due_date: formattedDueDate,
      subtotal: subtotal.toFixed(2),
      payment_terms: paymentTerms,
      discount_type: discountType,
      total_discount: totalDiscount,
      subsidiary_id: selectedSubsidary,
      total_amount: totalAmountAfterDiscount,
      item_table: items
        .map((item) => {
          const selectedService = services.find(
            (service) => service.service_name === item.type
          );

          if (!selectedService) {
            console.warn(`Service not found for type: ${item.type}`);
            return null;
          }
          
          return {
            service_name: item.type,
            service_provided_id: item.service_provided_id || null,
            service_id: selectedService.service_id,
            quantity: item.quantity,
            fy_id:item.fy_id,
            discount: item.discount,
            tax: hasGST ? parseFloat(selectedService.gst_rate) || 0 : 0,
            amount: item.amount,
            total_discount: item.totalDiscount,
            rate: item.rate,
          };
        })
        .filter((item) => item !== null),
    };

    try {
      
      const response = await axiosInstance.put(
        `/api/edit_invoice/${invoice_id}`,
        updatedInvoiceData
      );
      if (response.status === 200) {
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setSnackbarMessage("Data Updated Successfully");
        setTimeout(() => {
          navigate("/invoice-management");
        }, 2000);
      } else if (response.status === 429) {
        setSnackbarMessage(
          "Attention: Cannot create Invoice as the sub-company has already reached its invoice limit."
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        alert("Failed to update the invoice");
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
      setSnackbarMessage(
        "Attention: Cannot create Invoice as the sub-company has already reached its invoice limit."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
   const handleCompanyChange = (selectedOption) => {
    if (!selectedOption) return;
    setSelectedSubsidary(selectedOption.value);
    
  };
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axiosInstance.get(`/api/get_all_service`);
        setServices(response.data.data);
        setLoadingServices(false);
      } catch (error) {
        setServicesError("Failed to fetch services");
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axiosInstance.get(`/api/get_client_name`);
        setClients(response.data.data);
        setLoading(false);
      } catch (error) {
        setError("Failed to fetch clients");
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleback = () => {
    window.history.back();
  };
  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const updateTotals = () => {
    let amount = 0;
    let discount = 0;

    // Calculate individual item amounts and update only if necessary
    const updatedItems = items.map((item) => {
      const baseAmount = item.quantity * item.rate;
      const itemDiscount = (baseAmount * item.discount) / 100;
      const amountAfterItemDiscount = baseAmount - itemDiscount;
      const gstAmount = (amountAfterItemDiscount * item.tax) / 100;

      discount += itemDiscount;
      amount += amountAfterItemDiscount + gstAmount;

      return {
        ...item,
        amount: amountAfterItemDiscount + gstAmount,
      };
    });

    // Calculate total discount based on discount type
    let totalDiscountAmount = 0;

    if (discountType === "rs") {
      totalDiscountAmount = totalDiscount;
    } else if (discountType === "percentage") {
      totalDiscountAmount = (amount * totalDiscount) / 100;
    }

    // Ensure discount does not exceed total amount
    totalDiscountAmount = Math.min(totalDiscountAmount, amount);

    const finalAmountAfterDiscount = amount - totalDiscountAmount;

    // Update states if values changed
    if (amount !== totalAmount) {
      setTotalAmount(amount);
    }

    if (totalDiscountAmount !== totalDiscountAmountState) {
      setTotalDiscountAmountState(totalDiscountAmount);
    }

    if (finalAmountAfterDiscount !== totalAmountAfterDiscount) {
      setTotalAmountAfterDiscount(finalAmountAfterDiscount);
    }

    // Only update items if necessary
    if (JSON.stringify(updatedItems) !== JSON.stringify(items)) {
      setItems(updatedItems);
    }
  };

  useEffect(() => {
    updateTotals();
  }, [totalDiscount, discountType, items]);

  // useEffect(() => {
  //   updateTotals();
  // }, [totalDiscount, discountType, items]);

  const getMenuItems = (clients) => {
    return [
      ...clients.map((client) => ({
        value: client.client_id,
        label: client.client_name,
        subsidiaryId: client.subsidiary_id,
      })),
      { value: "add_new", label: "➕ Add New Client" },
    ];
  };

  const handleInputClickzeo = (e) => {
    e.target.select();
  };
  return (
    <div
      className={`table-container invoice-form-page edit-invoice-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body invoice-form-content">
        <div className="invoice-page-heading">
          <h1 className="form-title jost-bold">Edit Invoice Management</h1>
          <p className="invoice-page-subtitle">
            Update invoice details, addresses and billable service items.
          </p>
        </div>
        <div className="invoice-form-card edit-invoice-form-card">
          <div className="form-body-invoice invoice-form-body">
            <form onSubmit={handleSubmit} className="form-box invoice-form-box">
              <div className="form-body-container invoice-form-sections">
                <section className="invoice-form-section">
                  <div className="invoice-section-header">
                    <div>
                      <h2 className="invoice-section-title">
                        Invoice Details
                      </h2>
                      <p className="invoice-section-description">
                        Review the selected client, subsidiary and invoice
                        identifiers before saving updates.
                      </p>
                    </div>
                  </div>

                  <div className="invoice-primary-grid">
                    <div className="form-group invoice-client-field">
                      <label className="form-label-invoice">
                        Client
                        <span className="required">*</span>
                      </label>
                      {loading ? (
                        <div className="loader-container">
                          <p>Loading clients...</p>
                        </div>
                      ) : error ? (
                        <p className="error-message">{error}</p>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={selectedClient || "N/A"}
                            readOnly
                            placeholder="Client Name"
                            className={`input-field ${
                              errors.selectedClient ? "input-error" : ""
                            }`}
                          />

                          {errors.selectedClient && (
                            <p className="error-message">
                              {errors.selectedClient}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label-invoice">
                        Subsidary<span className="required">*</span>
                      </label>

                      {isLoading ? (
                        <div className="loader-container">
                          <ClipLoader color="#007bff" size={30} />
                          <p>Fetching Subsidaries...</p>
                        </div>
                      ) : (
                        <>
                          <Select
                            value={
                              selectedSubsidary
                                ? {
                                    label:
                                      subsidaries.find(
                                        (company) =>
                                          company.subsidiary_id ===
                                          selectedSubsidary
                                      )?.subsidiary_name || "Select Subsidary",
                                    value: selectedSubsidary,
                                  }
                                : null
                            }
                            onChange={handleCompanyChange}
                            options={subsidaries.map((company) => ({
                              label: company.subsidiary_name,
                              value: company.subsidiary_id,
                            }))}
                            className={`select-field-invoice ${
                              errors.selectedSubsidary ? "input-error" : ""
                            }`}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 1200 }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 1200,
                                position: "absolute",
                              }),
                              container: (base) => ({
                                ...base,
                                zIndex: 1,
                                position: "relative",
                              }),
                            }}
                            menuPortalTarget={document.body}
                            placeholder="Select Subsidary"
                          />

                          {errors.selectedSubsidary && (
                            <p className="error-message">
                              {errors.selectedSubsidary}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="invoice-secondary-grid" style={{ marginTop: "16px" }}>
                    <div className="form-group">
                      <label className="form-label-invoice">Company Type</label>
                      <div className="form-inputs">
                        <input
                          type="text"
                          className={`input-fieldsss ${
                            errors.selectedCompanyType ? "input-error" : ""
                          }`}
                          value={selectedCompanyType || ""}
                          readOnly
                          placeholder="Company Type"
                        />
                        {errors.selectedCompanyType && (
                          <p className="error-message">
                            {errors.selectedCompanyType}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label-invoice">
                        Invoice Number
                        <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={invoiceNumber}
                        readOnly
                        placeholder="Invoice Number will be auto-filled"
                        className={`input-field input-autofill ${
                          errors.invoiceNumber ? "input-error" : ""
                        }`}
                      />
                      {errors.invoiceNumber && (
                        <p className="error-message">{errors.invoiceNumber}</p>
                      )}
                    </div>
                  </div>
                </section>

                {billingAddress && shippingAddress && (
                  <section className="invoice-form-section">
                    <div className="invoice-section-header">
                      <div>
                        <h2 className="invoice-section-title">
                          Address Information
                        </h2>
                        <p className="invoice-section-description">
                          Review the linked billing and shipping address details
                          for this invoice.
                        </p>
                      </div>
                    </div>
                    <div className="address-container invoice-address-grid">
                      <div className="address-block edit-invoice-address-card">
                        <p className="edit-invoice-address-kicker">
                          Billing Address
                        </p>
                        <h3 className="address-title">Billing Address</h3>
                        <div className="address-details">
                          <strong>{billingAddress.company}</strong>
                          <p>{billingAddress.area}</p>
                          <p>{billingAddress.landmark}</p>
                          <p>{billingAddress.office}</p>
                          <p>{billingAddress.pin}</p>
                          <p>
                            <strong>Phone:</strong> {billingAddress.phone}
                          </p>
                        </div>
                      </div>

                      <div className="address-block edit-invoice-address-card">
                        <p className="edit-invoice-address-kicker">
                          Shipping Address
                        </p>
                        <h3 className="address-title">Shipping Address</h3>
                        <div className="address-details">
                          <div>
                            <strong>{shippingAddress.company}</strong>
                          </div>
                          <p>{shippingAddress.address}</p>
                          <div>
                            <p>{billingAddress.area}</p>
                            <p>{billingAddress.landmark}</p>
                            <p>{billingAddress.office}</p>
                            <p>{billingAddress.pin}</p>
                            <strong>Phone:</strong> {shippingAddress.phone}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                <section className="invoice-form-section">
                  <div className="invoice-section-header">
                    <div>
                      <h2 className="invoice-section-title">
                        Billing Period &amp; Terms
                      </h2>
                      <p className="invoice-section-description">
                        Update the invoice date, terms and due date while
                        keeping the existing calculation logic intact.
                      </p>
                    </div>
                  </div>
                  <div className="invoice-billing-grid">
                    <div className="form-group">
                      <label className="form-label-invoice">
                        Invoice Date<span className="required">*</span>
                      </label>
                      <div className="date-input-container">
                        <input
                          ref={invoiceDateInputRef}
                          type="text"
                          value={invoiceDate || ""}
                          onChange={(e) =>
                            handleInputChange("invoiceDate", e.target.value)
                          }
                          placeholder="Enter the Date as DD-MM-YYYY"
                          maxLength={10}
                          className={`input-field-datepicker ${
                            errors.invoiceDate || invoiceDateError
                              ? "input-error"
                              : ""
                          }`}
                        />
                        <div className="date-example-text">(DD-MM-YYYY)</div>
                      </div>

                      {(errors.invoiceDate || invoiceDateError) && (
                        <p className="error-message">
                          {errors.invoiceDate || invoiceDateError}
                        </p>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label-invoice terms-label">
                        Terms<span className="required">*</span>
                      </label>
                      <div className="invoice-terms-input">
                        <input
                          type="text"
                          value={
                            paymentTerms !== null &&
                            paymentTerms !== undefined
                              ? String(paymentTerms)
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange("paymentTerms", e.target.value)
                          }
                          placeholder="Enter days"
                          className={`input-field ${
                            errors.paymentTerms ? "input-error" : ""
                          }`}
                        />
                        <span className="invoice-terms-suffix">Days</span>
                      </div>

                      {errors.paymentTerms && (
                        <p className="error-message">{errors.paymentTerms}</p>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label-invoice">
                        Due Date<span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={dueDate || ""}
                        onChange={(e) =>
                          handleInputChange("dueDate", e.target.value)
                        }
                        placeholder="Enter Due Date"
                        className={`input-field ${
                          errors.dueDate ? "input-error" : ""
                        }`}
                        readOnly
                      />
                      {errors.dueDate && (
                        <p className="error-message">{errors.dueDate}</p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="invoice-form-section invoice-form-section-last">
                  <div className="invoice-section-header">
                    <div>
                      <h2 className="invoice-section-title">Item Table</h2>
                      <p className="invoice-section-description">
                        Review, update and add service-wise billing items for
                        this invoice.
                      </p>
                    </div>
                  </div>
                  <div className="item-table invoice-items-card">
                    <h2 className="jost-bold">Item Table</h2>
                    <div className="invoice-table-scroll">
                      <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th className="serviceid jost-bold">Type Of Services</th>
                      <th className="serviceid jost-bold">
                        Financial Year 
                      </th>
                      <th>Quantity</th>
                      <th>Rate</th>
                      <th>Tax</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) =>{ 
                      
                      return(
                      <tr key={index} className="row-table">
                        <td className="table-border">
                          <Select
                            className=""
                            value={
                              item.serviceId
                                ? {
                                    label:
                                      services.find(
                                        (service) =>
                                          service.service_id === item.serviceId
                                      )?.service_name || item.type,
                                    value: item.serviceId,
                                  }
                                : null
                            }
                            onChange={(selectedOption) => {
                              if (selectedOption.value === "add-new") {
                                handleAddNewService();
                              } else {
                                handleServiceSelection(index, selectedOption);
                              }
                            }}
                            options={[
                              { value: "add-new", label: "➕ Add New Service" },
                              ...services.map((service) => ({
                                value: service.service_id,
                                label: service.service_name,
                              })),
                            ]}
                            placeholder="Select The Type Of Services"
                            classNamePrefix="react-select edit-table-select"
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              menu: (base) => ({
                                ...base,
                                marginTop: 0,
                                borderRadius: 6,
                                zIndex: 9999,
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: "200px",
                                overflowY: "auto",
                                padding: 0,
                                "& > div:first-of-type": {
                                  position: "sticky",
                                  top: 0,
                                  backgroundColor: "#f8f9fa",
                                  zIndex: 2,
                                  borderBottom: "1px solid #e0e0e0",
                                },
                              }),
                              control: (base) => ({
                                ...base,
                                minHeight: "40px",
                                borderColor: "#ccc",
                                color: "#000",
                              }),
                              option: (
                                base,
                                { data, isSelected, isFocused }
                              ) => ({
                                ...base,
                                color: isSelected ? "#fff" : "#000",
                                fontWeight:
                                  data.value === "add-new" ? "bold" : "normal",
                                backgroundColor: isSelected
                                  ? "#2684FF"
                                  : data.value === "add-new"
                                  ? "#f8f9fa"
                                  : base.backgroundColor,
                                padding:
                                  data.value === "add-new"
                                    ? "10px 12px"
                                    : "8px 12px",
                                "&:hover": {
                                  backgroundColor: isSelected
                                    ? "#2684FF"
                                    : data.value === "add-new"
                                    ? "#f0f0f0"
                                    : "#f5f5f5",
                                  color: isSelected ? "#fff" : "#000",
                                },
                                position:
                                  data.value === "add-new"
                                    ? "sticky"
                                    : "relative",
                                top: data.value === "add-new" ? 0 : "auto",
                                zIndex: data.value === "add-new" ? 2 : 1,
                                cursor: "pointer",
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color: "#000",
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color: "#666",
                              }),
                            }}
                            menuPortalTarget={serviceMenuPortalTarget}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                            components={{
                              Option: ({ children, ...props }) => (
                                <components.Option {...props}>
                                  {props.data.value === "add-new" ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <span style={{ fontSize: "12px" }}>
                                        ➕
                                      </span>
                                      <span>Add New Service</span>
                                    </div>
                                  ) : (
                                    children
                                  )}
                                </components.Option>
                              ),
                            }}
                          />
                        </td>
                             <td className="table-border">
                          {/* <Select
                            value={
                              item.serviceId
                                ? {
                                    label:
                                      services.find(
                                        (service) =>
                                          service.service_id === item.serviceId
                                      )?.service_name || item.type,
                                    value: item.serviceId,
                                  }
                                : null
                            }
                            onChange={(selectedOption) => {
                              if (selectedOption.value === "add-new") {
                                handleAddNewService(); // Call function to handle adding a new service
                              } else {
                                handleServiceSelection(index, selectedOption);
                              }
                            }}
                            options={[
                              ...services.map((service) => ({
                                value: service.service_id,
                                label: service.service_name,
                              })),
                              { value: "add-new", label: "➕ Add New Service" }, // Different label for distinction
                            ]}
                            getOptionLabel={(e) =>
                              e.value === "add-new" ? (
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: "#007bff",
                                  }}
                                >
                                  {e.label}
                                </span>
                              ) : (
                                e.label
                              )
                            }
                            placeholder="Select The Type Of Services"
                            classNamePrefix="react-select"
                            styles={{
                              menu: (base) => ({
                                ...base,
                                marginTop: 0,
                                borderRadius: 6,
                                zIndex: 1000, // Ensure dropdown is above other elements
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: "200px", // Set a max height for better scrolling
                                overflowY: "auto",
                              }),
                              control: (base) => ({
                                ...base,
                                minHeight: "40px", // Increase height for better appearance
                                borderColor: "#ccc",
                              }),
                              option: (base, { data }) => ({
                                ...base,
                                color:
                                  data.value === "add-new"
                                    ? "#007bff"
                                    : "inherit",
                                fontWeight:
                                  data.value === "add-new" ? "bold" : "normal",
                                backgroundColor:
                                  data.value === "add-new"
                                    ? "#f8f9fa"
                                    : base.backgroundColor,
                              }),
                            }}
                          /> */}
                          <Select
                            value={
                              item.fy_id
                                ? {
                                  label:
                                    financialYears.find(
                                      (fy) =>
                                        fy.id === item.fy_id
                                    )?.fy_label || "Select Financial Year",
                                  value: item.fy_id,
                                }
                                : null
                            }
                            onChange={(selectedOption) => {handleFYSelection(index, selectedOption);}}
                            options={
                             financialYears.map((fy) => ({
                                value: fy.id,
                                label: fy.fy_label,
                              }))
                            }
                            placeholder="Select The Financial Year"
                            classNamePrefix="react-select"
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                            }}
                            menuPortalTarget={serviceMenuPortalTarget}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                          />
                        </td>
                        <td className="table-border">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const value = e.target.value;

                              // Allow empty value when user deletes
                              if (value === "") {
                                handleItemChange(index, "quantity", "");
                                return;
                              }

                              // Allow only numbers + decimal
                              if (/^\d*\.?\d*$/.test(value)) {
                                handleItemChange(index, "quantity", value);
                              }
                            }}
                            step="0.01"
                          />
                        </td>

                        <td className="table-border">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleItemChange(
                                index,
                                "rate",
                                newValue === "" ? "" : parseFloat(newValue) || 0
                              );
                            }}
                            step="0.01"
                          />
                        </td>

                        {/* <td className="table-border discount-cell">
                          <input
                            type="number"
                            value={item.discount}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleItemChange(
                                index,
                                "discount",
                                newValue === "" ? "" : parseFloat(newValue) || 0
                              );
                            }}
                            step="0.01"
                            min="0"
                            className="discount-input"
                          />
                        </td> */}

                        <td className="table-border">
                          <input
                            type="number"
                            value={item.tax || 0} // Show tax as 0 if no GST
                            readOnly
                            step="0.01"
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            value={Number(item.amount).toFixed(2)} // Ensures max two decimal places
                            readOnly
                            step="0.01"
                          />
                        </td>

                        <td>
                          <div
                            className="cancel-btn-row"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <MdCancel />
                          </div>
                        </td>
                      </tr>
                    )})}

                    <tr className="row-table">
                      <td colSpan={7}>
                        <button
                          type="button"
                          className="add-row-btn"
                          onClick={addNewRow}
                        >
                          <span>+</span> Add New Row
                        </button>
                      </td>
                    </tr>
                  </tbody>
                      </table>
                    </div>
                    <div className="invoice-total-main">
                      <div className="invoice-totals tableBottom">
                        {/* Subtotal */}
                        <div className="total-row tableBottom-row">
                          <div className="total-label tableBottom-label">
                            Sub Total
                          </div>
                          <div className="total-amount tableBottom-amount">
                            <input
                              type="number"
                              value={(Number(totalAmount) || 0).toFixed(2)}
                              readOnly
                              step="0.01"
                            />
                          </div>
                        </div>

                        {/* Total Discount */}
                        <div className="total-row tableBottom-row">
                          <div className="total-label tableBottom-label">
                            Total Discount
                            <br />
                            <Select
                              options={discountOptions}
                              value={discountOptions.find(
                                (option) => option.value === discountType
                              )}
                              onChange={(selectedOption) => {
                                setdiscountType(selectedOption.value);
                                setTotalDiscount(0);
                              }}
                              className="discount-select"
                              isSearchable={false}
                            />
                          </div>
                          <div
                            className="total-amount tableBottom-amount"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <input
                              type="number"
                              value={totalDiscount}
                              onChange={(e) => {
                                let value = e.target.value.trim();
                                let discountValue = parseFloat(value) || 0;

                                if (discountType === "percentage") {
                                  discountValue = Math.min(
                                    100,
                                    Math.max(0, discountValue)
                                  );
                                } else {
                                  discountValue = Math.min(
                                    totalAmount,
                                    Math.max(0, discountValue)
                                  );
                                }

                                setTotalDiscount(discountValue);
                                setTimeout(() => updateTotals(), 0);
                              }}
                              step="0.01"
                              min="0"
                              max={
                                discountType === "percentage"
                                  ? 100
                                  : totalAmount
                              }
                              style={{
                                width: "70px",
                                padding: "5px",
                                textAlign: "right",
                              }}
                            />
                          </div>
                        </div>

                        {/* Total Amount After Discount */}
                        <div className="total-row tableBottom-row">
                          <div className="total-label tableBottom-label">
                            Total Amount
                          </div>
                          <div className="total-amount tableBottom-amount">
                            <input
                              type="number"
                              value={totalAmountAfterDiscount.toFixed(2)}
                              readOnly
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </form>
          </div>
          <div className="form-footer">
            <div className="button-group">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={handleBack}
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="btn btn-save"
                onClick={handleSubmit}
              >
                SAVE
              </button>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <ServiceMasterModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveService}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(50px)", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
